import { RootState } from '../store';
import { updateLiveStreamSettings } from '../store/reducers/livestreamSettings';
import { useEffect, useRef, MutableRefObject, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const useMediaStream = (): [MutableRefObject<HTMLVideoElement | null>, MediaStream | null] => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const dispatch = useDispatch();
  const settings = useSelector((state: RootState) => state.live.settings);

  useEffect(() => {
    const getLocalMedia = async () => {
      try {
        // Enumerate devices to check if we have a video input (camera) and audio input (microphone)
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some((device) => device.kind === 'videoinput');
        const hasAudioInput = devices.some((device) => device.kind === 'audioinput');

        dispatch(
          updateLiveStreamSettings({
            isMicAccessGranted: hasAudioInput,
            isCameraAccessGranted: hasVideoInput,
            audio: hasAudioInput ? settings.audio : false,
            video: hasVideoInput ? settings.video : false,
          })
        );

        // Request media devices
        if (hasAudioInput || hasVideoInput) {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: hasAudioInput ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true } : false,
            video: hasVideoInput ? { width: { ideal: 640 }, height: { ideal: 480 } } : false,
          });
          setStream(mediaStream);

          // Attach stream to video element
          if (videoRef.current && mediaStream.getVideoTracks().length > 0) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.muted = true;
          }
        } else {
          // No devices available, set stream to null
          setStream(null);
        }
      } catch (error) {
        console.error("Media setup error:", error);
        dispatch(
          updateLiveStreamSettings({
            isMicAccessGranted: false,
            isCameraAccessGranted: false,
            audio: false,
            video: false,
          })
        );
        setStream(null);
      }
    };

    getLocalMedia();

    // Cleanup function to stop media tracks when component unmounts or settings change
    // return () => {
    //    if (stream instanceof MediaStream) {
    //     stream.getTracks().forEach((track) => track.stop());
    //   }
    // };
  }, [settings.video, settings.audio]);

  return [videoRef, stream];
};

export default useMediaStream;
